import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Clock, FileText, X } from "lucide-react";
import { getEventColor } from "./constants";

// Clickable Event Item with Popover for details
const EventItem = ({ event, variant = "card" }) => {
  const [open, setOpen] = useState(false);
  const eventColor = getEventColor(event.color);
  
  // Different display styles based on variant
  const renderTrigger = () => {
    switch (variant) {
      case "card":
        // Card style - visible but compact
        return (
          <div
            className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
            style={{ backgroundColor: `${eventColor}15` }}
          >
            <div
              className="w-1.5 h-full min-h-[32px] rounded-full"
              style={{ backgroundColor: eventColor }}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{event.name}</p>
              <p className="text-xs text-muted-foreground">
                {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
              </p>
            </div>
            {event.notes && (
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        );
      
      case "compact":
        // Compact inline style (used in My Shifts, Week views)
        return (
          <div
            className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: eventColor }}
            />
            <span className="font-medium">{event.name}</span>
            <span className="text-xs text-muted-foreground">
              {event.start_time.slice(0, 5)}-{event.end_time.slice(0, 5)}
            </span>
            {event.notes && (
              <FileText className="w-3 h-3 text-muted-foreground" />
            )}
          </div>
        );
      
      case "mini":
        // Mini style for timeline (just name)
        return (
          <div
            className="text-[10px] text-white rounded px-1 py-0.5 mb-0.5 truncate cursor-pointer hover:opacity-80 transition-opacity"
            style={{ backgroundColor: eventColor }}
            title={`${event.name}: ${event.start_time} - ${event.end_time}`}
          >
            {event.name}
          </div>
        );
      
      case "timeline-bar":
        // Timeline bar style (absolute positioned)
        return (
          <span className="truncate cursor-pointer">
            {event.name}
          </span>
        );
        
      default:
        return null;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {renderTrigger()}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="relative">
          {/* Colored header bar */}
          <div 
            className="h-2 rounded-t-md"
            style={{ backgroundColor: eventColor }}
          />
          
          {/* Close button */}
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-2 p-1 rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
          
          {/* Content */}
          <div className="p-4 space-y-3">
            <div>
              <h4 className="font-semibold text-lg pr-6">{event.name}</h4>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Clock className="w-4 h-4" />
                <span className="font-medium">
                  {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
                </span>
              </div>
            </div>
            
            {event.notes ? (
              <div className="pt-3 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Manager Notes
                </p>
                <p className="text-sm whitespace-pre-wrap bg-muted/50 rounded-md p-3">
                  {event.notes}
                </p>
              </div>
            ) : (
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground italic">No additional notes</p>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EventItem;
